
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Home } from 'lucide-react';

interface ServicesSectionProps {
  locationName: string;
}

const ServicesSection: React.FC<ServicesSectionProps> = ({ locationName }) => {
  const asphaltShingles = [
    "GAF Timberline HDZ and Grand Sequoia shingles",
    "Owens Corning Duration and TruDefinition systems",
    "CertainTeed Landmark and Grand Manor collections",
    "Atlas StormMaster and Pinnacle Pristine shingles",
    "Malarkey Legacy and Windsor shingle systems"
  ];

  const metalRoofing = [
    "Standing seam metal roofing (26-gauge and 24-gauge steel)",
    "Corrugated metal panel systems",
    "Aluminum and copper metal roofing",
    "DECRA stone-coated steel shingles",
    "Classic Rib and PBR panel systems"
  ];

  const repairServices = [
    "Storm damage repair and insurance claims assistance",
    "Leak detection and repair using thermal imaging",
    "Flashing repair and replacement",
    "Gutter repair and installation",
    "Chimney and vent boot sealing"
  ];

  const commercialServices = [
    "Firestone EPDM and TPO membrane systems",
    "GAF Liberty TPO and EverGuard EPDM",
    "Johns Manville PVC and EPDM systems",
    "Carlisle SynTec single-ply membranes",
    "IB Roof Systems PVC and TPO products"
  ];

  const industrialServices = [
    "Manufacturing facility roofing",
    "Warehouse and distribution center roofing",
    "Agricultural building roofing",
    "Municipal building roofing",
    "Healthcare facility roofing"
  ];

  return (
    <>
      <section className="mb-12">
        <h2 className="text-3xl font-bold mb-6 flex items-center">
          <Home className="h-8 w-8 text-primary mr-3" />
          Complete Roofing Services for {locationName}
        </h2>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-2xl">Residential Roofing Services</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-3">Roof Replacement & Installation</h3>
                <p className="mb-4">
                  When your {locationName} home needs a complete roof replacement, our certified installers use only premium materials designed for North Central North Carolina's demanding climate. According to HomeAdvisor's 2025 data, quality roof replacement costs range from $9,516-11,719 nationally, but our local expertise ensures maximum value and longevity in the Twin Counties region.
                </p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold mb-2">Asphalt Shingles:</h4>
                    <ul className="space-y-1 text-sm">
                      {asphaltShingles.map((item, index) => (
                        <li key={index}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Metal Roofing:</h4>
                    <ul className="space-y-1 text-sm">
                      {metalRoofing.map((item, index) => (
                        <li key={index}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">Roof Repair Services</h3>
                <p className="mb-4">
                  Don't let minor issues become major problems. Our {locationName} team provides comprehensive roof repair services averaging $392-1,929 according to current industry data, but early intervention can save thousands compared to emergency repairs.
                </p>
                <ul className="space-y-1">
                  {repairServices.map((service, index) => (
                    <li key={index}>• {service}</li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      <section className="mb-12">
        <h2 className="text-3xl font-bold mb-6">Commercial Roofing Solutions</h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Commercial Membrane Systems</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4">
                {locationName}'s commercial and industrial buildings require specialized roofing solutions that can handle North Central North Carolina's intense summer heat and severe weather patterns. Our commercial roofing team installs and maintains:
              </p>
              <ul className="space-y-2">
                {commercialServices.map((service, index) => (
                  <li key={index}>• {service}</li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Industrial & Manufacturing Roofing</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4">
                With {locationName}'s strong industrial base, we specialize in large-scale commercial projects:
              </p>
              <ul className="space-y-2">
                {industrialServices.map((service, index) => (
                  <li key={index}>• {service}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>
    </>
  );
};

export default ServicesSection;
